import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { CustomerContext } from './session.js';

interface CustomerContextValue {
  /** Current customer banner context (empty object until a customer is found). */
  customer: CustomerContext;
  /** Replaces the current customer context (e.g. after a successful ID&V). */
  setCustomer: (customer: CustomerContext) => void;
  /**
   * Shallow-merges into the current customer context (e.g. enriching the banner
   * with verification flags after Medium Security or the Dashboard fetch).
   */
  updateCustomer: (patch: Partial<CustomerContext>) => void;
  /** Clears the customer context (used by Clear session). */
  clearCustomer: () => void;
}

const Ctx = createContext<CustomerContextValue | undefined>(undefined);

/**
 * Resets the CUSTOMER / ID&V identity + verification context back to a blank
 * slate WITHOUT dropping a live online journey (#251). When a Genesys call is in
 * progress the customer context carries `contextConversationId` (stamped by
 * App's enrichment effect for every online interaction) — the correlation anchor
 * the SPA forwards as `X-Genesys-Interaction-Id` so the gateway / Call History
 * stay correlated to the live call. Wiping the whole context (`{}`) would drop
 * that anchor and drop the journey to Offline; instead we preserve ONLY the
 * call-scoped anchors (`contextConversationId`) and call properties (`cli`,
 * `callsInLastWeek`), discarding every identity / verification / IVR-hint field.
 * Offline (no live conversation) resets fully to `{}`.
 *
 * This is the single source of truth for "clear the customer but keep the call",
 * shared by App's Clear Session (CCF-10) and the broker security completion
 * (CCF-318 #555 — the Done button on the Reset Internet Password screen returns
 * the agent to ID&V, and the banner must not keep showing the prior broker).
 */
export function resetIdentityPreservingOnlineJourney(
  current: CustomerContext,
): CustomerContext {
  if (!current.contextConversationId) return {};
  return {
    cli: current.cli,
    callsInLastWeek: current.callsInLastWeek,
    contextConversationId: current.contextConversationId,
  };
}

/**
 * sessionStorage key for the identified-customer context. Persisting it lets
 * the context survive a page reload within the same tab/interaction (e.g. the
 * agent opening Transactions as a fresh screen, or refreshing), rather than the
 * context being lost to the in-memory React state on every navigation.
 */
const STORAGE_KEY = 'ptsb.customerContext';

function loadPersisted(): CustomerContext {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CustomerContext) : {};
  } catch {
    return {};
  }
}

function persist(customer: CustomerContext): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(customer));
  } catch {
    // Non-fatal: storage may be unavailable (private mode); the in-memory
    // context still works for the current navigation.
  }
}

/**
 * Holds the customer/verification context that the agent banner displays.
 *
 * Screens (e.g. ID&V) push the identified customer into this context; the
 * banner reads it. This keeps the banner driven by data rather than threading
 * setters through the route registry.
 */
export function CustomerProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [customer, setCustomerState] = useState<CustomerContext>(loadPersisted);
  // These setters are STABLE (empty-deps `useCallback`) — they never close over
  // `customer` (the updater form threads the current value). A stable identity
  // matters: `App`'s Genesys-enrichment effect depends on `updateCustomer`, and
  // if it changed on every render the effect would re-run (and its cleanup would
  // cancel the in-flight interaction fetch) on any unrelated re-render — the
  // #244 online ID&V regression where the O24 number / PAN never came through
  // because the enrichment was cancelled before it fetched.
  const setCustomer = useCallback((next: CustomerContext): void => {
    persist(next);
    setCustomerState(next);
  }, []);
  const updateCustomer = useCallback(
    (patch: Partial<CustomerContext>): void =>
      setCustomerState((current) => {
        const next = { ...current, ...patch };
        persist(next);
        return next;
      }),
    [],
  );
  const clearCustomer = useCallback((): void => {
    persist({});
    setCustomerState({});
  }, []);
  const value = useMemo(
    () => ({ customer, setCustomer, updateCustomer, clearCustomer }),
    [customer, setCustomer, updateCustomer, clearCustomer],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Access the customer banner context. Must be used within `CustomerProvider`. */
export function useCustomer(): CustomerContextValue {
  const value = useContext(Ctx);
  if (!value)
    throw new Error('useCustomer must be used within CustomerProvider');
  return value;
}
